import { useState, useCallback, useMemo } from 'react';
import { Table, Space, Tag, Button, Modal, Form, Input, InputNumber, Select, Switch, DatePicker, message, Popconfirm, Collapse, Typography, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { ImageUpload } from '@/components/base';
import { useWisecureQuery, useWisecureMutation, useInvalidateQueries } from '@wisecure/api-client/hooks';
import type { FeedItemResponse, CreateFeedItemDto, UpdateFeedItemDto, FeedItemGeoRegion } from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import type { PaginatedResponse } from '@/types/dashboard';
import { info, success, error as errorColors, neutral } from '@/theme';

const TYPE_OPTIONS = [
  { value: 'ADVICE', label: 'Conseil' },
  { value: 'PROMO', label: 'Promo' },
  { value: 'INFO', label: 'Info' },
  { value: 'ALERT', label: 'Alerte' },
];

const TYPE_COLORS: Record<string, string> = {
  ADVICE: info[400],
  PROMO: success[400],
  INFO: neutral[500],
  ALERT: errorColors[400],
};

const TYPE_DEFAULT_ACTION_LABEL: Record<string, string> = {
  ADVICE: 'En savoir plus',
  PROMO: 'Voir l\'offre',
  INFO: 'En savoir plus',
  ALERT: 'Voir les détails',
};

interface GeoRegionItem {
  id: string;
  code: string;
  name: string;
  type: string;
}

interface FeedFilters {
  type?: 'ADVICE' | 'PROMO' | 'INFO' | 'ALERT';
  isActive?: boolean;
  page: number;
  limit: number;
}

interface FeedFormValues {
  title: string;
  description?: string;
  imageUrl?: string;
  type: 'ADVICE' | 'PROMO' | 'INFO' | 'ALERT';
  actionUrl?: string;
  actionLabel?: string;
  geoRegionIds?: string[];
  priority?: number;
  isActive?: boolean;
  startsAt?: dayjs.Dayjs;
  endsAt?: dayjs.Dayjs;
}

export function FeedPage() {
  const [filters, setFilters] = useState<FeedFilters>({
    page: 1,
    limit: 20,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FeedItemResponse | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [form] = Form.useForm<FeedFormValues>();
  const invalidate = useInvalidateQueries();

  const [geoSearch, setGeoSearch] = useState('');
  const [selectedGeoItems, setSelectedGeoItems] = useState<GeoRegionItem[]>([]);

  const { data: feedResponse, isLoading } = useWisecureQuery<PaginatedResponse<FeedItemResponse>>(
    queryKeys.feed(filters as unknown as Record<string, unknown>),
    (client) =>
      client.feed.feedControllerFindAll({
        page: filters.page,
        limit: filters.limit,
        type: filters.type,
        isActive: filters.isActive,
      }) as Promise<PaginatedResponse<FeedItemResponse>>,
  );

  const { data: geoRegionsData } = useWisecureQuery<PaginatedResponse<GeoRegionItem>>(
    ['geo-regions', 'search', geoSearch],
    (client) =>
      client.geoRegions.geoRegionsControllerFindAll({
        search: geoSearch,
        type: 'COMMUNE',
        limit: 20,
      }) as Promise<PaginatedResponse<GeoRegionItem>>,
    { enabled: geoSearch.length > 0 },
  );

  const geoRegionOptions = useMemo(() => {
    const options = selectedGeoItems.map((gr) => ({
      value: gr.id,
      label: `${gr.name} (${gr.code})`,
    }));

    if (geoRegionsData?.data) {
      for (const gr of geoRegionsData.data) {
        if (!options.some((o) => o.value === gr.id)) {
          options.push({
            value: gr.id,
            label: `${gr.name} (${gr.code})`,
          });
        }
      }
    }

    return options;
  }, [geoRegionsData, selectedGeoItems]);

  const createMutation = useWisecureMutation<FeedItemResponse, CreateFeedItemDto>(
    (client, data) =>
      client.feed.feedControllerCreate({ requestBody: data }),
    {
      onSuccess: () => {
        message.success('Élément créé');
        invalidate(queryKeys.feed(filters as unknown as Record<string, unknown>));
        setModalOpen(false);
        form.resetFields();
      },
      onError: () => {
        message.error("Erreur lors de la création");
      },
    },
  );

  const updateMutation = useWisecureMutation<FeedItemResponse, { id: string; data: UpdateFeedItemDto }>(
    (client, { id, data }) =>
      client.feed.feedControllerUpdate({ id, requestBody: data }),
    {
      onSuccess: () => {
        message.success('Élément mis à jour');
        invalidate(queryKeys.feed(filters as unknown as Record<string, unknown>));
        setModalOpen(false);
        setEditingItem(null);
        form.resetFields();
      },
      onError: () => {
        message.error("Erreur lors de la mise à jour");
      },
    },
  );

  const deleteMutation = useWisecureMutation<unknown, string>(
    (client, id) => client.feed.feedControllerRemove({ id }),
    {
      onSuccess: () => {
        message.success('Élément supprimé');
        invalidate(queryKeys.feed(filters as unknown as Record<string, unknown>));
      },
      onError: () => {
        message.error("Erreur lors de la suppression");
      },
    },
  );

  const handleCreate = useCallback(() => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({
      type: 'INFO',
      isActive: true,
      priority: 0,
      startsAt: dayjs(),
      endsAt: dayjs().add(7, 'day'),
      actionLabel: TYPE_DEFAULT_ACTION_LABEL['INFO'],
    });
    setSelectedGeoItems([]);
    setAdvancedOpen(false);
    setModalOpen(true);
  }, [form]);

  const handleEdit = useCallback((item: FeedItemResponse) => {
    setEditingItem(item);
    const geoRegions: FeedItemGeoRegion[] = item.geoRegions ?? [];
    const geoRegionIds = geoRegions.map((gr) => gr.id);

    form.setFieldsValue({
      title: item.title,
      description: typeof item.description === 'string' ? item.description : undefined,
      imageUrl: typeof item.imageUrl === 'string' ? item.imageUrl : undefined,
      type: item.type,
      actionUrl: typeof item.actionUrl === 'string' ? item.actionUrl : undefined,
      actionLabel: typeof item.actionLabel === 'string' ? item.actionLabel : undefined,
      geoRegionIds,
      priority: item.priority,
      isActive: item.isActive,
      startsAt: item.startsAt ? dayjs(item.startsAt as unknown as string) : undefined,
      endsAt: item.endsAt ? dayjs(item.endsAt as unknown as string) : undefined,
    });
    setSelectedGeoItems(geoRegions.map((gr) => ({ id: gr.id, code: gr.code, name: gr.name, type: gr.type })));
    setAdvancedOpen(
      !!(item.actionUrl || item.actionLabel || geoRegions.length > 0 || item.startsAt || item.endsAt || item.priority),
    );
    setModalOpen(true);
  }, [form]);

  const handleSubmit = useCallback(async () => {
    const values = await form.validateFields();
    const payload: CreateFeedItemDto = {
      title: values.title,
      type: values.type,
      description: values.description,
      imageUrl: values.imageUrl,
      actionUrl: values.actionUrl,
      actionLabel: values.actionLabel,
      geoRegionIds: values.geoRegionIds,
      priority: values.priority,
      isActive: values.isActive,
      startsAt: values.startsAt?.toISOString(),
      endsAt: values.endsAt?.toISOString(),
    };

    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }, [form, editingItem, createMutation, updateMutation]);

  const handleTypeChange = useCallback((type: string) => {
    const currentLabel = form.getFieldValue('actionLabel');
    const isDefaultLabel = Object.values(TYPE_DEFAULT_ACTION_LABEL).includes(currentLabel) || !currentLabel;
    if (isDefaultLabel) {
      form.setFieldValue('actionLabel', TYPE_DEFAULT_ACTION_LABEL[type] ?? '');
    }
  }, [form]);

  const columns = [
    {
      title: 'Titre',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => (
        <Tag
          style={{
            backgroundColor: `${TYPE_COLORS[type] ?? neutral[300]}14`,
            color: TYPE_COLORS[type] ?? neutral[500],
            borderColor: `${TYPE_COLORS[type] ?? neutral[300]}40`,
            borderRadius: 100,
          }}
        >
          {TYPE_OPTIONS.find((t) => t.value === type)?.label ?? type}
        </Tag>
      ),
    },
    {
      title: 'Communes',
      key: 'geoRegions',
      width: 180,
      render: (_: unknown, record: FeedItemResponse) => {
        const geoRegions: FeedItemGeoRegion[] = record.geoRegions ?? [];
        if (geoRegions.length === 0) {
          return <Tag>Global</Tag>;
        }
        return (
          <Space size={2} wrap>
            {geoRegions.slice(0, 2).map((gr) => (
              <Tag key={gr.id}>{gr.name}</Tag>
            ))}
            {geoRegions.length > 2 && <Tag>+{geoRegions.length - 2}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Priorité',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      sorter: true,
    },
    {
      title: 'Actif',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 70,
      render: (active: boolean) => (
        <Tag
          style={{
            backgroundColor: `${active ? success[400] : neutral[300]}14`,
            color: active ? success[400] : neutral[500],
            borderColor: `${active ? success[400] : neutral[300]}40`,
            borderRadius: 100,
          }}
        >
          {active ? 'Oui' : 'Non'}
        </Tag>
      ),
    },
    {
      title: 'Début',
      dataIndex: 'startsAt',
      key: 'startsAt',
      width: 110,
      render: (date: string) => date ? new Date(date).toLocaleDateString('fr-FR') : '—',
    },
    {
      title: 'Fin',
      dataIndex: 'endsAt',
      key: 'endsAt',
      width: 110,
      render: (date: string) => date ? new Date(date).toLocaleDateString('fr-FR') : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: FeedItemResponse) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="Supprimer cet élément ?" onConfirm={() => deleteMutation.mutate(record.id)}>
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Fil d'actualités"
        subtitle={`${feedResponse?.total ?? 0} éléments`}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Ajouter
          </Button>
        }
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          placeholder="Type"
          allowClear
          style={{ width: 150 }}
          value={filters.type}
          onChange={(v) => setFilters((prev) => ({ ...prev, type: v, page: 1 }))}
          options={TYPE_OPTIONS}
        />
        <Select
          placeholder="Statut"
          allowClear
          style={{ width: 150 }}
          value={filters.isActive}
          onChange={(v) => setFilters((prev) => ({ ...prev, isActive: v, page: 1 }))}
          options={[
            { value: true, label: 'Actif' },
            { value: false, label: 'Inactif' },
          ]}
        />
      </Space>

      <Table
        columns={columns}
        dataSource={feedResponse?.data ?? []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: filters.page,
          pageSize: filters.limit,
          total: feedResponse?.total,
          onChange: (page) => setFilters((prev) => ({ ...prev, page })),
          showSizeChanger: false,
        }}
        locale={{ emptyText: 'Aucun élément' }}
      />

      <Modal
        title={editingItem ? "Modifier l'élément" : "Créer un élément"}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditingItem(null);
          form.resetFields();
        }}
        onOk={handleSubmit}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={600}
        okText={editingItem ? 'Mettre à jour' : 'Créer'}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="Titre"
            rules={[{ required: true, message: 'Titre requis' }]}
          >
            <Input placeholder="Titre de l'élément" autoFocus />
          </Form.Item>

          <Space style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="type"
              label="Type"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <Select options={TYPE_OPTIONS} onChange={handleTypeChange} />
            </Form.Item>
            <Form.Item
              name="isActive"
              label="Visible"
              valuePropName="checked"
              style={{ flex: 1, display: 'flex', alignItems: 'center' }}
            >
              <Switch />
            </Form.Item>
          </Space>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Description (optionnel)" />
          </Form.Item>

          <Form.Item name="imageUrl" label="Image">
            <ImageUpload folder="feed" />
          </Form.Item>

          <Collapse
            ghost
            activeKey={advancedOpen ? ['advanced'] : []}
            onChange={(keys) => setAdvancedOpen(keys.includes('advanced'))}
            items={[
              {
                key: 'advanced',
                label: (
                  <Space>
                    <SettingOutlined />
                    <span>Paramètres avancés</span>
                    {!advancedOpen && (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Action, communes, planification, priorité
                      </Typography.Text>
                    )}
                  </Space>
                ),
                children: (
                  <>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                      Lien d'action et communes ciblées
                    </Typography.Text>
                    <Space style={{ display: 'flex', gap: 16 }}>
                      <Form.Item
                        name="actionUrl"
                        label="URL d'action"
                        style={{ flex: 1 }}
                        tooltip={{
                          title: (
                            <span>
                              Lien ouvert au tap dans l'app cliente. Accepte une URL classique ou un deep link (ex:&nbsp;
                              <code>/report/:reportId</code>
                              ).{' '}
                              <Link
                                to="/docs/deep-links"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'inherit', textDecoration: 'underline' }}
                              >
                                Voir la documentation
                              </Link>
                            </span>
                          ),
                          icon: <QuestionCircleOutlined />,
                        }}
                      >
                        <Input placeholder="https://... ou /report/ID" />
                      </Form.Item>
                      <Form.Item name="actionLabel" label="Texte du bouton" style={{ flex: 1 }}>
                        <Input placeholder="Ex: En savoir plus" />
                      </Form.Item>
                    </Space>
                    <Space style={{ display: 'flex', gap: 16, width: '100%' }}>
                      <Form.Item
                        name="geoRegionIds"
                        label="Communes ciblées"
                        style={{ flex: 2 }}
                        extra="Laisser vide pour un affichage global"
                      >
                        <Select
                          mode="multiple"
                          placeholder="Tapez pour rechercher des communes..."
                          showSearch
                          filterOption={false}
                          onSearch={setGeoSearch}
                          options={geoRegionOptions}
                          notFoundContent={geoSearch ? 'Chargement...' : 'Aucune commune trouvée'}
                          optionFilterProp="label"
                          maxTagCount="responsive"
                        />
                      </Form.Item>
                      <Form.Item name="priority" label="Priorité" style={{ flex: 1 }}>
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                      </Form.Item>
                    </Space>

                    <Divider style={{ margin: '8px 0 16px' }} />
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                      Planification
                    </Typography.Text>
                    <Space style={{ display: 'flex', gap: 16 }}>
                      <Form.Item name="startsAt" label="Date de début" style={{ flex: 1 }}>
                        <DatePicker showTime style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name="endsAt" label="Date de fin" style={{ flex: 1 }}>
                        <DatePicker showTime style={{ width: '100%' }} />
                      </Form.Item>
                    </Space>
                  </>
                ),
              },
            ]}
          />
        </Form>
      </Modal>
    </div>
  );
}
